import type { PaymentMethod } from '../types';
import { defaultAuthConfig } from '../types/auth';
import { API_BASE_URL } from '../config/api';

interface PaymentServiceConfig {
  baseUrl: string;
  tokenKey: string;
  userKey: string;
}

interface PaymentHistoryQuery {
  page?: number;
  limit?: number;
  from?: string;
  to?: string;
  status?: string;
}

interface SplitTransferQuery extends PaymentHistoryQuery {
  search?: string;
}

export interface CreatePaymentIntentInput {
  orderId: string;
  amount: number;
  currency?: string;
  paymentMethod: PaymentMethod;
  podProjectId?: string;
  podOrderId?: string;
  metadata?: Record<string, string>;
}

export interface CreatePaymentIntentResult {
  clientSecret?: string;
  paymentIntentId?: string;
  paymentReference?: string;
  paymentUrl?: string;
  status?: string;
}

export interface PaymentHistoryEntry {
  id: string;
  transactionRef: string;
  amount: number;
  currency: string;
  status: string;
  orderStatus: string;
  platformCommission?: number;
  creatorEarnings?: number;
  customer: {
    email: string;
    name: string;
  };
  createdAt: string;
  orderId: string;
  user: {
    id: string;
    fullname: string;
    email: string;
  };
}

export interface PaymentHistoryStats {
  total: number;
  successful: number;
  pending: number;
  failed: number;
  totalAmount: number;
}

export interface PaymentHistoryPagination {
  page: number;
  limit: number;
  total: number;
}

export interface PaymentHistoryResult {
  success: boolean;
  data: PaymentHistoryEntry[];
  stats?: PaymentHistoryStats;
  pagination?: PaymentHistoryPagination;
}

export interface SplitTransferParty {
  id?: string;
  name: string;
  email: string;
}

export interface SplitTransferEntry {
  id: string;
  transactionRef: string;
  orderId: string;
  amount: number;
  currency: string;
  paymentStatus: string;
  transferStatus: string;
  platformCommission: number;
  creatorEarnings: number;
  sellerPaid: boolean;
  sellerReceivedPayment: boolean;
  sellerPaidAt?: string;
  createdAt: string;
  itemCount: number;
  note: string;
  customer: SplitTransferParty | null;
  seller: SplitTransferParty | null;
}

export interface SplitTransferItem {
  paymentId: string;
  bookId?: string;
  title: string;
  amount: number;
  platformCommission: number;
  creatorEarnings: number;
  status: string;
  sellerPaid: boolean;
  sellerReceivedPayment: boolean;
  sellerPaidAt?: string;
  buyer: SplitTransferParty | null;
  seller: SplitTransferParty | null;
}

export interface SplitTransferDetails extends SplitTransferEntry {
  items: SplitTransferItem[];
}

export interface SplitTransferStats {
  total: number;
  completed: number;
  processing: number;
  pending: number;
  failed: number;
  grossAmount: number;
  platformRevenue: number;
  creatorPayouts: number;
  paidOutAmount: number;
  pendingPayoutAmount: number;
}

export interface SplitTransferResult {
  success: boolean;
  data: SplitTransferEntry[];
  stats?: SplitTransferStats;
  pagination?: PaymentHistoryPagination;
}

export interface CurrencyConfigResult {
  ok: boolean;
  usdToRwfRate?: number;
  paymentCurrency?: string;
  error?: string;
}

const defaultPaymentServiceConfig: PaymentServiceConfig = {
  baseUrl: API_BASE_URL,
  tokenKey: defaultAuthConfig.tokenKey,
  userKey: defaultAuthConfig.userKey,
};

class PaymentService {
  private baseUrl: string;
  private tokenKey: string;
  private userKey: string;

  constructor(config = defaultPaymentServiceConfig) {
    this.baseUrl = config.baseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private asNumber(value: unknown, fallbackValue = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
    return fallbackValue;
  }

  private buildHeaders(withJson = false): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    if (withJson) {
      headers['Content-Type'] = 'application/json';
    }

    if (typeof window === 'undefined') {
      return headers;
    }

    const token = window.localStorage.getItem(this.tokenKey);
    const rawUser = window.localStorage.getItem(this.userKey);

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (rawUser) {
      try {
        const parsedUser = this.asRecord(JSON.parse(rawUser));
        const userId = this.asString(parsedUser.id);
        const role = this.asString(parsedUser.role);
        if (userId) headers['x-user-id'] = userId;
        if (role) headers['x-user-role'] = role;
      } catch {
        // ignore malformed cache
      }
    }

    return headers;
  }

  private getStoredRole(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const rawUser = window.localStorage.getItem(this.userKey);
    if (!rawUser) {
      return '';
    }

    try {
      const parsedUser = this.asRecord(JSON.parse(rawUser));
      return this.asString(parsedUser.role)?.toLowerCase() || '';
    } catch {
      return '';
    }
  }

  private resolveHistoryPaths(): string[] {
    const role = this.getStoredRole();

    if (role === 'admin' || role === 'manager' || role === 'staff') {
      return ['/payments/history', '/payments/transactions/admin'];
    }

    if (role === 'vendor' || role === 'creator') {
      return ['/payments/transactions/vendor'];
    }

    return ['/payments/transactions/buyer'];
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    return (
      this.asString(record.message) ||
      this.asString(dataRecord.message) ||
      this.asString(record.error) ||
      this.asString(dataRecord.error) ||
      fallback
    );
  }

  private async parseResponse(response: Response): Promise<unknown> {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const fallback = `Request failed with status ${response.status}`;
      throw new Error(this.toErrorMessage(data, fallback));
    }

    return data;
  }

  private async requestWithPathFallback(paths: string[], init: RequestInit): Promise<unknown> {
    let lastError: Error | null = null;

    for (let index = 0; index < paths.length; index += 1) {
      const path = paths[index];
      const hasNextPath = index < paths.length - 1;

      try {
        const response = await fetch(`${this.baseUrl}${path}`, init);
        if ((response.status === 404 || response.status === 405) && hasNextPath) {
          continue;
        }

        return await this.parseResponse(response);
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Request failed');
      }
    }

    throw lastError ?? new Error('Request failed');
  }

  private normalizeMethod(value: unknown): PaymentMethod | null {
    const raw = this.asString(value);
    if (!raw) {
      return null;
    }

    const normalized = raw.toLowerCase();
    if (normalized === 'card' || normalized === 'cards' || normalized === 'flutterwave') {
      return 'card';
    }
    if (
      normalized === 'cod' ||
      normalized === 'cashondelivery' ||
      normalized === 'cash_on_delivery' ||
      normalized === 'cash-on-delivery'
    ) {
      return 'cod';
    }
    if (normalized === 'paypal') {
      return 'paypal';
    }
    if (
      normalized === 'mobilemoney' ||
      normalized === 'mobile_money' ||
      normalized === 'mobile-money' ||
      normalized === 'momo'
    ) {
      return 'mobileMoney';
    }

    return null;
  }

  private normalizeMethods(payload: unknown): PaymentMethod[] {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    const candidates = [
      this.asArray(record.methods),
      this.asArray(record.paymentMethods),
      this.asArray(dataRecord.methods),
      this.asArray(dataRecord.paymentMethods),
      this.asArray(payload),
    ];

    const resolved: PaymentMethod[] = [];
    for (const candidate of candidates) {
      for (const entry of candidate) {
        const entryRecord = this.asRecord(entry);
        const method =
          this.normalizeMethod(entry) ||
          this.normalizeMethod(entryRecord.method) ||
          this.normalizeMethod(entryRecord.code) ||
          this.normalizeMethod(entryRecord.id) ||
          this.normalizeMethod(entryRecord.type) ||
          this.normalizeMethod(entryRecord.name);

        if (method && !resolved.includes(method)) {
          resolved.push(method);
        }
      }

      if (resolved.length > 0) {
        return resolved;
      }
    }

    return ['card', 'cod'];
  }

  async getMethods(): Promise<PaymentMethod[]> {
    try {
      const response = await fetch(`${this.baseUrl}/payments/methods`, {
        method: 'GET',
        headers: this.buildHeaders(),
      });

      if (response.status === 404 || response.status === 500) {
        return ['card', 'cod'];
      }

      const payload = await this.parseResponse(response);
      return this.normalizeMethods(payload);
    } catch {
      return ['card', 'cod'];
    }
  }

  private normalizePaymentHistoryEntry(entryValue: unknown): PaymentHistoryEntry | null {
    const record = this.asRecord(entryValue);
    const customerRecord = this.asRecord(record.customer);
    const buyerRecord = this.asRecord(record.buyer);
    const userRecord = this.asRecord(record.user);

    const id =
      this.asString(record.id) ||
      this.asString(record._id) ||
      this.asString(record.paymentId) ||
      this.asString(record.payment_id) ||
      this.asString(record.transactionId) ||
      this.asString(record.transaction_id) ||
      this.asString(record.reference) ||
      this.asString(record.tx_ref) ||
      this.asString(record.orderId) ||
      this.asString(record.order_id);

    if (!id) {
      return null;
    }

    const customerEmail =
      this.asString(customerRecord.email) ||
      this.asString(buyerRecord.email) ||
      this.asString(record.email) ||
      '';
    const customerName =
      this.asString(customerRecord.name) ||
      this.asString(customerRecord.fullname) ||
      this.asString(buyerRecord.name) ||
      this.asString(buyerRecord.fullname) ||
      this.asString(record.customerName) ||
      'Customer';
    const userId =
      this.asString(userRecord.id) ||
      this.asString(userRecord._id) ||
      this.asString(userRecord.userId) ||
      '';
    const userFullname =
      this.asString(userRecord.fullname) ||
      this.asString(userRecord.fullName) ||
      this.asString(userRecord.name) ||
      '';
    const userEmail = this.asString(userRecord.email) || '';

    return {
      id,
      transactionRef:
        this.asString(record.transactionRef) ||
        this.asString(record.paymentReference) ||
        this.asString(record.reference) ||
        this.asString(record.tx_ref) ||
        '',
      amount: this.asNumber(
        record.amount,
        this.asNumber(
          record.grossAmount,
          this.asNumber(record.totalAmount, this.asNumber(record.total, this.asNumber(record.price))),
        ),
      ),
      currency: this.asString(record.currency) || 'RWF',
      status: this.asString(record.status) || this.asString(record.paymentStatus) || 'unknown',
      orderStatus: this.asString(record.orderStatus) || this.asString(record.order_status) || 'unknown',
      platformCommission: this.asNumber(record.platformCommission, this.asNumber(record.adminShare)),
      creatorEarnings: this.asNumber(record.creatorEarnings, this.asNumber(record.vendorShare)),
      customer: {
        email: customerEmail,
        name: customerName,
      },
      createdAt: this.asString(record.createdAt) || this.asString(record.date) || new Date().toISOString(),
      orderId:
        this.asString(record.orderId) ||
        this.asString(record.order_id) ||
        this.asString(record.id) ||
        '',
      user: {
        id: userId,
        fullname: userFullname,
        email: userEmail,
      },
    };
  }

  private normalizePaymentHistory(payload: unknown): PaymentHistoryResult {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    const historyCandidates = [
      this.asArray(record.data),
      this.asArray(record.items),
      this.asArray(record.transactions),
      this.asArray(record.payments),
      this.asArray(record.history),
      this.asArray(record.results),
      this.asArray(record.rows),
      this.asArray(dataRecord.items),
      this.asArray(dataRecord.transactions),
      this.asArray(dataRecord.payments),
      this.asArray(dataRecord.history),
      this.asArray(dataRecord.results),
      this.asArray(dataRecord.rows),
      this.asArray(dataRecord.data),
      this.asArray(payload),
    ];

    let normalizedRows: PaymentHistoryEntry[] = [];
    for (const candidate of historyCandidates) {
      if (candidate.length === 0) {
        continue;
      }

      normalizedRows = candidate
        .map((entry) => this.normalizePaymentHistoryEntry(entry))
        .filter((entry): entry is PaymentHistoryEntry => Boolean(entry));

      if (normalizedRows.length > 0) {
        break;
      }
    }

    const statsRecord = this.asRecord(record.stats);
    const dataStatsRecord = this.asRecord(dataRecord.stats);
    const paginationRecord = this.asRecord(record.pagination);
    const dataPaginationRecord = this.asRecord(dataRecord.pagination);
    const resolvedStatsRecord = Object.keys(statsRecord).length > 0 ? statsRecord : dataStatsRecord;
    const resolvedPaginationRecord =
      Object.keys(paginationRecord).length > 0 ? paginationRecord : dataPaginationRecord;

    const stats: PaymentHistoryStats | undefined =
      Object.keys(resolvedStatsRecord).length === 0
        ? undefined
        : {
            total: this.asNumber(resolvedStatsRecord.total),
            successful: this.asNumber(resolvedStatsRecord.successful),
            pending: this.asNumber(resolvedStatsRecord.pending),
            failed: this.asNumber(resolvedStatsRecord.failed),
            totalAmount: this.asNumber(
              resolvedStatsRecord.totalAmount,
              this.asNumber(resolvedStatsRecord.totalPaid, this.asNumber(resolvedStatsRecord.grossAmount)),
            ),
          };

    const pagination: PaymentHistoryPagination | undefined =
      Object.keys(resolvedPaginationRecord).length === 0
        ? undefined
        : {
            page: this.asNumber(resolvedPaginationRecord.page),
            limit: this.asNumber(resolvedPaginationRecord.limit),
            total: this.asNumber(resolvedPaginationRecord.total),
          };

    return {
      success: record.success !== false,
      data: normalizedRows,
      ...(stats ? { stats } : {}),
      ...(pagination ? { pagination } : {}),
    };
  }

  private normalizeSplitTransferParty(value: unknown): SplitTransferParty | null {
    const record = this.asRecord(value);
    const email = this.asString(record.email) || '';
    const name =
      this.asString(record.name) ||
      this.asString(record.fullname) ||
      this.asString(record.fullName) ||
      '';
    const id =
      this.asString(record.id) ||
      this.asString(record._id) ||
      this.asString(record.userId);

    if (!name && !email && !id) {
      return null;
    }

    return {
      ...(id ? { id } : {}),
      name: name || email || 'Unknown',
      email,
    };
  }

  private asBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return false;
  }

  private normalizeSplitTransferEntry(entryValue: unknown): SplitTransferEntry | null {
    const record = this.asRecord(entryValue);
    const id =
      this.asString(record.id) ||
      this.asString(record.orderId) ||
      this.asString(record.order_id) ||
      this.asString(record.transactionRef) ||
      this.asString(record.reference) ||
      this.asString(record.tx_ref);

    if (!id) {
      return null;
    }

    return {
      id,
      transactionRef:
        this.asString(record.transactionRef) ||
        this.asString(record.reference) ||
        this.asString(record.tx_ref) ||
        id,
      orderId:
        this.asString(record.orderId) ||
        this.asString(record.order_id) ||
        this.asString(record.transactionRef) ||
        id,
      amount: this.asNumber(record.amount, this.asNumber(record.grossAmount)),
      currency: this.asString(record.currency) || 'RWF',
      paymentStatus:
        this.asString(record.paymentStatus) ||
        this.asString(record.status) ||
        'pending',
      transferStatus:
        this.asString(record.transferStatus) ||
        this.asString(record.transfer_state) ||
        this.asString(record.status) ||
        'pending',
      platformCommission: this.asNumber(record.platformCommission, this.asNumber(record.adminShare)),
      creatorEarnings: this.asNumber(record.creatorEarnings, this.asNumber(record.vendorShare)),
      sellerPaid: this.asBoolean(record.sellerPaid),
      sellerReceivedPayment: this.asBoolean(record.sellerReceivedPayment),
      sellerPaidAt:
        this.asString(record.sellerPaidAt) ||
        this.asString(record.claimedAt) ||
        this.asString(record.claimed_at),
      createdAt: this.asString(record.createdAt) || new Date().toISOString(),
      itemCount: this.asNumber(record.itemCount, this.asNumber(record.itemsCount, 0)),
      note:
        this.asString(record.note) ||
        this.asString(record.description) ||
        this.asString(record.narration) ||
        '',
      customer:
        this.normalizeSplitTransferParty(record.customer) ||
        this.normalizeSplitTransferParty(record.buyer),
      seller: this.normalizeSplitTransferParty(record.seller),
    };
  }

  private normalizeSplitTransferItem(value: unknown): SplitTransferItem | null {
    const record = this.asRecord(value);
    const paymentId =
      this.asString(record.paymentId) ||
      this.asString(record.payment_id) ||
      this.asString(record.id);

    if (!paymentId) {
      return null;
    }

    return {
      paymentId,
      ...(this.asString(record.bookId) || this.asString(record.book_id)
        ? { bookId: this.asString(record.bookId) || this.asString(record.book_id) || '' }
        : {}),
      title: this.asString(record.title) || this.asString(record.name) || 'Untitled content',
      amount: this.asNumber(record.amount),
      platformCommission: this.asNumber(record.platformCommission, this.asNumber(record.adminShare)),
      creatorEarnings: this.asNumber(record.creatorEarnings, this.asNumber(record.vendorShare)),
      status: this.asString(record.status) || 'pending',
      sellerPaid: this.asBoolean(record.sellerPaid),
      sellerReceivedPayment: this.asBoolean(record.sellerReceivedPayment),
      sellerPaidAt:
        this.asString(record.sellerPaidAt) ||
        this.asString(record.claimedAt) ||
        this.asString(record.claimed_at),
      buyer:
        this.normalizeSplitTransferParty(record.buyer) ||
        this.normalizeSplitTransferParty(record.customer),
      seller: this.normalizeSplitTransferParty(record.seller),
    };
  }

  private normalizeSplitTransferHistory(payload: unknown): SplitTransferResult {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    const candidates = [
      this.asArray(record.data),
      this.asArray(record.items),
      this.asArray(record.rows),
      this.asArray(record.results),
      this.asArray(dataRecord.items),
      this.asArray(dataRecord.rows),
      this.asArray(dataRecord.results),
      this.asArray(dataRecord.data),
      this.asArray(payload),
    ];

    let data: SplitTransferEntry[] = [];
    for (const candidate of candidates) {
      if (candidate.length === 0) {
        continue;
      }

      data = candidate
        .map((entry) => this.normalizeSplitTransferEntry(entry))
        .filter((entry): entry is SplitTransferEntry => Boolean(entry));

      if (data.length > 0) {
        break;
      }
    }

    const statsRecord = this.asRecord(record.stats);
    const nestedStatsRecord = this.asRecord(dataRecord.stats);
    const resolvedStats = Object.keys(statsRecord).length > 0 ? statsRecord : nestedStatsRecord;
    const paginationRecord = this.asRecord(record.pagination);
    const nestedPaginationRecord = this.asRecord(dataRecord.pagination);
    const resolvedPagination =
      Object.keys(paginationRecord).length > 0 ? paginationRecord : nestedPaginationRecord;

    const stats: SplitTransferStats | undefined =
      Object.keys(resolvedStats).length === 0
        ? undefined
        : {
            total: this.asNumber(resolvedStats.total),
            completed: this.asNumber(resolvedStats.completed, this.asNumber(resolvedStats.successful)),
            processing: this.asNumber(resolvedStats.processing),
            pending: this.asNumber(resolvedStats.pending),
            failed: this.asNumber(resolvedStats.failed),
            grossAmount: this.asNumber(resolvedStats.grossAmount, this.asNumber(resolvedStats.totalAmount)),
            platformRevenue: this.asNumber(resolvedStats.platformRevenue),
            creatorPayouts: this.asNumber(resolvedStats.creatorPayouts),
            paidOutAmount: this.asNumber(resolvedStats.paidOutAmount),
            pendingPayoutAmount: this.asNumber(resolvedStats.pendingPayoutAmount),
          };

    const pagination: PaymentHistoryPagination | undefined =
      Object.keys(resolvedPagination).length === 0
        ? undefined
        : {
            page: this.asNumber(resolvedPagination.page),
            limit: this.asNumber(resolvedPagination.limit),
            total: this.asNumber(resolvedPagination.total),
          };

    return {
      success: record.success !== false,
      data,
      ...(stats ? { stats } : {}),
      ...(pagination ? { pagination } : {}),
    };
  }

  private normalizeSplitTransferDetails(payload: unknown): SplitTransferDetails {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    const source = Object.keys(dataRecord).length > 0 ? dataRecord : record;
    const base = this.normalizeSplitTransferEntry(source);

    if (!base) {
      throw new Error('Unable to load transfer details');
    }

    const itemsCandidates = [
      this.asArray(source.items),
      this.asArray(record.items),
      this.asArray(dataRecord.items),
    ];

    let items: SplitTransferItem[] = [];
    for (const candidate of itemsCandidates) {
      if (candidate.length === 0) {
        continue;
      }

      items = candidate
        .map((entry) => this.normalizeSplitTransferItem(entry))
        .filter((entry): entry is SplitTransferItem => Boolean(entry));

      if (items.length > 0) {
        break;
      }
    }

    return {
      ...base,
      items,
    };
  }

  async getHistory(): Promise<PaymentHistoryResult> {
    const payload = await this.requestWithPathFallback(this.resolveHistoryPaths(), {
      method: 'GET',
      headers: this.buildHeaders(),
    });

    return this.normalizePaymentHistory(payload);
  }

  private buildQueryString(query?: PaymentHistoryQuery): string {
    if (!query) {
      return '';
    }

    const params = new URLSearchParams();
    const setIfPresent = (key: string, value: unknown) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        params.set(key, String(value));
        return;
      }
      if (typeof value === 'string' && value.trim()) {
        params.set(key, value.trim());
      }
    };

    setIfPresent('page', query.page);
    setIfPresent('limit', query.limit);
    setIfPresent('from', query.from);
    setIfPresent('to', query.to);
    setIfPresent('status', query.status);
    if ('search' in query) {
      setIfPresent('search', query.search);
    }

    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
  }

  async getAdminHistory(query?: PaymentHistoryQuery): Promise<PaymentHistoryResult> {
    const qs = this.buildQueryString(query);
    const payload = await this.requestWithPathFallback([`/payments/history${qs}`, `/payments/transactions/admin${qs}`], {
      method: 'GET',
      headers: this.buildHeaders(),
    });

    return this.normalizePaymentHistory(payload);
  }

  async getAdminHistoryAll(options?: { limit?: number; from?: string; to?: string; maxPages?: number }): Promise<PaymentHistoryResult> {
    const limit = Math.max(1, Math.min(500, Math.floor(options?.limit ?? 200)));
    const from = options?.from?.trim() || '1970-01-01';
    const to = options?.to?.trim();
    const maxPages = Math.max(1, Math.min(50, Math.floor(options?.maxPages ?? 20)));

    const combined: PaymentHistoryEntry[] = [];
    let page = 1;
    let total = 0;

    for (; page <= maxPages; page += 1) {
      const result = await this.getAdminHistory({ page, limit, from, ...(to ? { to } : {}) });

      if (page === 1) {
        total = result.pagination?.total ?? result.stats?.total ?? 0;
      }

      if (!result.data || result.data.length === 0) {
        break;
      }

      combined.push(...result.data);

      if (total > 0 && combined.length >= total) {
        break;
      }

      if (!result.pagination) {
        // No pagination info: stop once the server returns fewer than `limit`.
        if (result.data.length < limit) {
          break;
        }
      }
    }

    const unique = new Map<string, PaymentHistoryEntry>();
    combined.forEach((row) => {
      const key = `${row.orderId}-${row.transactionRef || row.id}-${row.createdAt}`;
      if (!unique.has(key)) {
        unique.set(key, row);
      }
    });

    const data = [...unique.values()].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const stats: PaymentHistoryStats = {
      total: data.length,
      successful: data.filter((row) => ['paid', 'success', 'successful', 'completed', 'succeeded'].includes(row.status.trim().toLowerCase())).length,
      pending: data.filter((row) => ['pending', 'processing'].includes(row.status.trim().toLowerCase())).length,
      failed: data.filter((row) => ['failed', 'cancelled', 'canceled', 'declined', 'error'].includes(row.status.trim().toLowerCase())).length,
      totalAmount: data.reduce((sum, row) => sum + row.amount, 0),
    };

    return {
      success: true,
      data,
      stats,
      pagination: { page: 1, limit: data.length, total: data.length },
    };
  }

  async getBuyerTransactions(): Promise<PaymentHistoryResult> {
    const payload = await this.requestWithPathFallback(['/payments/transactions/buyer'], {
      method: 'GET',
      headers: this.buildHeaders(),
    });

    return this.normalizePaymentHistory(payload);
  }

  async getAdminSplitTransfers(query?: SplitTransferQuery): Promise<SplitTransferResult> {
    const qs = this.buildQueryString(query);
    const payload = await this.requestWithPathFallback(
      [`/payments/split-transfers/admin${qs}`, `/payments/history${qs}`, `/payments/transactions/admin${qs}`],
      {
        method: 'GET',
        headers: this.buildHeaders(),
      },
    );

    return this.normalizeSplitTransferHistory(payload);
  }

  async getCreatorSplitTransfers(query?: SplitTransferQuery): Promise<SplitTransferResult> {
    const qs = this.buildQueryString(query);
    const payload = await this.requestWithPathFallback(
      [`/payments/split-transfers/creator${qs}`, `/payments/transactions/vendor${qs}`],
      {
        method: 'GET',
        headers: this.buildHeaders(),
      },
    );

    return this.normalizeSplitTransferHistory(payload);
  }

  async getSplitTransferDetails(transactionId: string): Promise<SplitTransferDetails> {
    const id = encodeURIComponent(transactionId.trim());
    const payload = await this.requestWithPathFallback(
      [`/payments/split-transfers/${id}`, `/payments/transactions/${id}`],
      {
        method: 'GET',
        headers: this.buildHeaders(),
      },
    );

    return this.normalizeSplitTransferDetails(payload);
  }

  async getPaidBooksMy(): Promise<unknown> {
    return this.requestWithPathFallback(['/payments/my'], {
      method: 'GET',
      headers: this.buildHeaders(),
    });
  }

  async getPaidBooksAllAdmin(): Promise<unknown> {
    return this.requestWithPathFallback(['/payments/succeed', '/payments/done', '/payments/my'], {
      method: 'GET',
      headers: this.buildHeaders(),
    });
  }

  async getUnpaidBooksAdmin(): Promise<unknown> {
    return this.requestWithPathFallback(['/payments/unpaid'], {
      method: 'GET',
      headers: this.buildHeaders(),
    });
  }

  async getCurrencyRate(): Promise<number> {
    const response = await fetch(`${this.baseUrl}/config/currency`, {
      method: 'GET',
      headers: this.buildHeaders(),
    });

    const payload = (await this.parseResponse(response)) as CurrencyConfigResult;
    if (!payload?.ok || typeof payload.usdToRwfRate !== 'number' || !Number.isFinite(payload.usdToRwfRate)) {
      throw new Error(payload?.error || 'Failed to load currency config.');
    }

    return payload.usdToRwfRate;
  }

  private buildIntentBody(input: CreatePaymentIntentInput): Record<string, unknown> {
    const metadata: Record<string, string> = {
      ...(input.metadata ?? {}),
    };

    if (input.podProjectId) {
      metadata.podProjectId = input.podProjectId;
    }
    if (input.podOrderId) {
      metadata.podOrderId = input.podOrderId;
    }

    return {
      orderId: input.orderId,
      amount: input.amount,
      currency: input.currency ?? 'RWF',
      paymentMethod: input.paymentMethod,
      method: input.paymentMethod,
      podProjectId: input.podProjectId,
      podOrderId: input.podOrderId,
      metadata,
    };
  }

  async initialize(input: CreatePaymentIntentInput): Promise<CreatePaymentIntentResult> {
    const response = await fetch(`${this.baseUrl}/payments/initialize`, {
      method: 'POST',
      headers: this.buildHeaders(true),
      body: JSON.stringify(this.buildIntentBody(input)),
    });

    const payload = await this.parseResponse(response);
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    const paymentIntentRecord = this.asRecord(record.paymentIntent);
    const dataIntentRecord = this.asRecord(dataRecord.paymentIntent);

    return {
      clientSecret:
        this.asString(record.clientSecret) ||
        this.asString(dataRecord.clientSecret) ||
        this.asString(record.paymentClientSecret) ||
        this.asString(dataRecord.paymentClientSecret) ||
        this.asString(paymentIntentRecord.client_secret) ||
        this.asString(paymentIntentRecord.clientSecret) ||
        this.asString(dataIntentRecord.client_secret) ||
        this.asString(dataIntentRecord.clientSecret),
      paymentIntentId:
        this.asString(record.paymentIntentId) ||
        this.asString(dataRecord.paymentIntentId) ||
        this.asString(record.paymentId) ||
        this.asString(dataRecord.paymentId) ||
        this.asString(paymentIntentRecord.id) ||
        this.asString(dataIntentRecord.id),
      paymentReference:
        this.asString(record.paymentReference) ||
        this.asString(dataRecord.paymentReference) ||
        this.asString(record.reference) ||
        this.asString(dataRecord.reference) ||
        this.asString(record.tx_ref) ||
        this.asString(dataRecord.tx_ref),
      paymentUrl:
        this.asString(record.paymentLink) ||
        this.asString(dataRecord.paymentLink) ||
        this.asString(record.paymentUrl) ||
        this.asString(dataRecord.paymentUrl) ||
        this.asString(record.redirectUrl) ||
        this.asString(dataRecord.redirectUrl) ||
        this.asString(record.link) ||
        this.asString(dataRecord.link) ||
        this.asString(paymentIntentRecord.link) ||
        this.asString(dataIntentRecord.link),
      status:
        this.asString(record.status) ||
        this.asString(dataRecord.status) ||
        this.asString(paymentIntentRecord.status) ||
        this.asString(dataIntentRecord.status),
    };
  }

  async createIntent(input: CreatePaymentIntentInput): Promise<CreatePaymentIntentResult> {
    return this.initialize(input);
  }

  async createPayment(payload: {
    books: Array<{ bookId: string | number; quantity: number }>;
    payment_provider?: string;
    redirect_url?: string;
  }): Promise<{
    ok: boolean;
    data: {
      success: boolean;
      tx_ref: string;
      totalAmount: number;
      currency: string;
      paymentUrl: string;
      payments: unknown[];
    };
  }> {
    const response = await fetch(`${this.baseUrl}/payments`, {
      method: 'POST',
      headers: this.buildHeaders(true),
      body: JSON.stringify({
        ...payload,
        payment_provider: payload.payment_provider || 'flutterwave',
      }),
    });

    const data = await this.parseResponse(response);
    return data as {
      ok: boolean;
      data: {
        success: boolean;
        tx_ref: string;
        totalAmount: number;
        currency: string;
        paymentUrl: string;
        payments: unknown[];
      };
    };
  }
}

export const paymentService = new PaymentService();
